// Propriétaires juridiques : personnes, SCI, indivisions, sociétés ; associés, pouvoirs, avances et remboursements (F-006).
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { post, put, upload } from "@core/api";
import { date, money, today } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, FileDrop, FileLine, Input, KeyValues, Loading, Modal, MoneyInput, Notice, PageHeader, Select, Table, Textarea, fieldError, useToast } from "@core/ui";
import { AddressFields, EMPTY_ADDRESS, OWNER_KINDS, numOrNull, str, type Address } from "./shared";
import { powersLabel } from "./propertyOwners";

type Owner = {
  id: string;
  kind: string;
  kind_label: string;
  display_name: string;
  legal_name: string | null;
  siren: string | null;
  email: string | null;
  phone: string | null;
  address: Address;
  address_line: string;
  details: Record<string, any>;
  archived_at: string | null;
  properties_count?: number;
  members_count?: number;
};

type Member = {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  role: string;
  role_label: string;
  share_milli: number | null;
  share_label: string | null;
  parts_count: number | null;
  can_sign_leases: boolean;
  can_collect_rents: boolean;
  can_order_works: boolean;
  receives_reports: boolean;
  powers_note: string;
  mandate_from: string | null;
  mandate_until: string | null;
  mandate_status: string;
  effective_powers: { sign_leases: boolean; collect_rents: boolean; order_works: boolean };
  ended_at: string | null;
};

type OwnerDetail = Owner & {
  members: Member[];
  members_shares_label: string | null;
  properties: { id: string; label: string; kind_label: string; share_label: string; archived: boolean }[];
  contributions: {
    id: string;
    member_id: string;
    member_name: string;
    property_label: string | null;
    kind: string;
    amount_cents: number;
    occurred_on: string;
    justification: string;
    cancelled_at: string | null;
    cancel_reason: string | null;
  }[];
  balances: { member_id: string; member_name: string; advances_cents: number; refunds_cents: number; balance_cents: number }[];
  documents: { id: string; title: string; filename: string; size_bytes: number; url: string; created_at: string }[];
  can_edit: boolean;
  notice: string;
};

const ROLES = [
  { value: "associe", label: "Associé" },
  { value: "gerant", label: "Gérant" },
  { value: "cogerant", label: "Cogérant" },
  { value: "indivisaire", label: "Indivisaire" },
  { value: "usufruitier", label: "Usufruitier" },
  { value: "nu_proprietaire", label: "Nu-propriétaire" },
  { value: "president", label: "Président" },
  { value: "mandataire", label: "Mandataire" },
  { value: "autre", label: "Autre" },
];

const MANDATE: Record<string, string> = { actif: "En vigueur", expire: "Expiré", a_venir: "À venir", termine: "Terminé" };

export function OwnersPage() {
  const org = useOrg();
  const nav = useNavigate();
  const q = useGet<{ items: Owner[] }>(org.api("/owners"));
  const [creating, setCreating] = useState(false);
  const items = q.data?.items ?? [];
  return (
    <div className="page">
      <PageHeader
        eyebrow="Espace"
        title="Propriétaires"
        intro="Les détenteurs juridiques de vos biens : vous-même, une SCI, une indivision ou une société. Ils sont distincts des membres qui agissent dans cet espace."
        actions={org.can("operate") ? <Button onClick={() => setCreating(true)}>Ajouter un propriétaire</Button> : null}
      />
      <ErrorBox error={q.error} />
      {q.isLoading ? <Loading /> : null}
      {!q.isLoading && items.length === 0 ? (
        <Empty title="Aucun propriétaire renseigné." action={org.can("operate") ? <Button variant="secondaire" onClick={() => setCreating(true)}>Ajouter un propriétaire</Button> : undefined}>
          Renseignez qui détient vos biens pour préparer les baux, les quittances et les rapports.
        </Empty>
      ) : null}
      {items.length ? (
        <Table caption="Propriétaires">
          <thead>
            <tr>
              <th>Propriétaire</th>
              <th>Type</th>
              <th>Biens détenus</th>
              <th>Associés et représentants</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id}>
                <td>
                  <Link to={org.to(`proprietaires/${o.id}`)}>{o.display_name}</Link>
                  {o.siren ? <span className="pat-sub mono">SIREN {o.siren}</span> : null}
                  {o.archived_at ? <span className="pat-sub">Archivé</span> : null}
                </td>
                <td>{o.kind_label}</td>
                <td className="num">{o.properties_count ?? 0}</td>
                <td className="num">{o.members_count ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      <Modal open={creating} onClose={() => setCreating(false)} title="Ajouter un propriétaire">
        {creating ? (
          <OwnerForm
            onDone={(o) => {
              setCreating(false);
              nav(org.to(`proprietaires/${o.id}`));
            }}
            onCancel={() => setCreating(false)}
          />
        ) : null}
      </Modal>
    </div>
  );
}

function OwnerForm({ owner, onDone, onCancel }: { owner?: Owner; onDone: (o: Owner) => void; onCancel: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [f, setF] = useState({
    kind: owner?.kind ?? "personne",
    display_name: owner?.display_name ?? "",
    legal_name: owner?.legal_name ?? "",
    siren: owner?.siren ?? "",
    email: owner?.email ?? "",
    phone: owner?.phone ?? "",
    address: owner?.address ?? EMPTY_ADDRESS,
    notes: str(owner?.details?.notes),
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const err = (k: string) => fieldError(error, k);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const saved = owner ? await put<Owner>(org.api(`/owners/${owner.id}`), f) : await post<Owner>(org.api("/owners"), f);
      invalidate(org.api("/owners"), org.api("/properties"));
      toast(owner ? "Propriétaire mis à jour." : "Propriétaire ajouté.");
      onDone(saved);
    } catch (e2) {
      setError(e2);
    } finally {
      setBusy(false);
    }
  }
  const company = f.kind === "sci" || f.kind === "societe";
  return (
    <form className="stack" onSubmit={submit} noValidate>
      <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
      <Select label="Type" value={f.kind} options={OWNER_KINDS} error={err("kind")} onChange={(e) => setF({ ...f, kind: e.target.value })} />
      <Input
        label="Nom d’usage"
        required
        value={f.display_name}
        error={err("display_name")}
        placeholder={company ? "Ex. SCI des Tilleuls" : f.kind === "indivision" ? "Ex. Indivision Martin" : "Ex. Camille Bailleur"}
        onChange={(e) => setF({ ...f, display_name: e.target.value })}
      />
      {company ? (
        <div className="form-row">
          <Input label="Dénomination sociale" value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} />
          <Input label="SIREN" inputMode="numeric" value={f.siren} error={err("siren")} onChange={(e) => setF({ ...f, siren: e.target.value })} />
        </div>
      ) : null}
      <div className="form-row">
        <Input label="Adresse e-mail (rapports)" type="email" value={f.email} error={err("email")} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Input label="Téléphone" type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
      </div>
      <AddressFields value={f.address} onChange={(a) => setF({ ...f, address: a })} errors={{ postal_code: err("address.postal_code") }} />
      <Textarea label="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      <div className="form-actions">
        <Button variant="discret" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" busy={busy}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

export function OwnerPage() {
  const org = useOrg();
  const { ownerId = "" } = useParams();
  const toast = useToast();
  const invalidate = useInvalidate();
  const q = useGet<OwnerDetail>(org.api(`/owners/${ownerId}`));
  const [editing, setEditing] = useState(false);
  const [member, setMember] = useState<Member | "nouveau" | null>(null);
  const [contribution, setContribution] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [docKind, setDocKind] = useState("statuts");
  const [busy, setBusy] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const o = q.data;
  const canEdit = o.can_edit && !o.archived_at;
  const activeMembers = o.members.filter((m) => !m.ended_at);
  return (
    <div className="page">
      <PageHeader
        eyebrow={`Propriétaire · ${o.kind_label}`}
        title={o.display_name}
        intro={o.address_line || undefined}
        actions={
          o.can_edit ? (
            o.archived_at ? (
              <Button
                variant="secondaire"
                onClick={async () => {
                  await post(org.api(`/owners/${o.id}/restore`));
                  invalidate(org.api("/owners"));
                }}
              >
                Restaurer
              </Button>
            ) : (
              <>
                <Button
                  variant="secondaire"
                  onClick={async () => {
                    try {
                      await post(org.api(`/owners/${o.id}/archive`));
                      invalidate(org.api("/owners"));
                      toast("Propriétaire archivé.");
                    } catch (e: any) {
                      toast(e.message, "erreur");
                    }
                  }}
                >
                  Archiver
                </Button>
                <Button onClick={() => setEditing(true)}>Modifier</Button>
              </>
            )
          ) : null
        }
      />
      {o.archived_at ? <Notice tone="attention">Propriétaire archivé le {date(o.archived_at)}.</Notice> : null}
      <div className="grid-2">
        <Card title="Identification">
          <KeyValues
            items={[
              ["Type", o.kind_label],
              ["Dénomination", o.legal_name ?? "—"],
              ["SIREN", o.siren ?? "—"],
              ["Adresse e-mail", o.email ?? "—"],
              ["Téléphone", o.phone ?? "—"],
            ]}
          />
          {o.details.notes ? <p className="small">{o.details.notes}</p> : null}
        </Card>
        <Card title="Biens détenus">
          {o.properties.length === 0 ? (
            <p className="muted">Aucun bien. Les quotes-parts se règlent depuis la fiche de chaque logement.</p>
          ) : (
            <ul className="pat-due">
              {o.properties.map((p) => (
                <li key={p.id}>
                  <span>
                    <Link to={org.to(`logements/${p.id}?onglet=proprietaires`)}>{p.label}</Link> <span className="muted small">· {p.kind_label}{p.archived ? " · archivé" : ""}</span>
                  </span>
                  <Badge tone="encre">{p.share_label}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      <Card
        title="Associés, indivisaires et représentants"
        actions={
          canEdit ? (
            <Button small variant="secondaire" onClick={() => setMember("nouveau")}>
              Ajouter une personne
            </Button>
          ) : null
        }
      >
        <p className="small muted">Aucun pouvoir n’est accordé par défaut. Un pouvoir n’est effectif que pendant la période du mandat.{o.members_shares_label ? ` Parts renseignées : ${o.members_shares_label}.` : ""}</p>
        {o.members.length === 0 ? (
          <p className="muted">Aucune personne renseignée.</p>
        ) : (
          <Table caption="Associés et représentants">
            <thead>
              <tr>
                <th>Personne</th>
                <th>Qualité</th>
                <th>Part</th>
                <th>Mandat</th>
                <th>Pouvoirs effectifs</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {o.members.map((m) => (
                <tr key={m.id}>
                  <td>
                    {m.full_name}
                    {m.receives_reports ? <span className="pat-sub">Reçoit les rapports</span> : null}
                    {m.powers_note ? <span className="pat-sub">{m.powers_note}</span> : null}
                  </td>
                  <td>{m.role_label}</td>
                  <td className="num">
                    {m.share_label ?? "—"}
                    {m.parts_count ? <span className="pat-sub">{m.parts_count} parts</span> : null}
                  </td>
                  <td className="small">
                    {MANDATE[m.mandate_status] ?? m.mandate_status}
                    {m.mandate_from || m.mandate_until ? (
                      <span className="pat-sub">
                        {m.mandate_from ? `du ${date(m.mandate_from)} ` : ""}
                        {m.mandate_until ? `au ${date(m.mandate_until)}` : ""}
                      </span>
                    ) : null}
                  </td>
                  <td className="small">{m.ended_at ? "—" : powersLabel(m.effective_powers)}</td>
                  <td className="right">
                    {canEdit && !m.ended_at ? (
                      <div className="pat-actions">
                        <Button small variant="discret" onClick={() => setMember(m)}>
                          Modifier
                        </Button>
                        <Button
                          small
                          variant="discret"
                          onClick={async () => {
                            try {
                              await post(org.api(`/owners/${o.id}/members/${m.id}/end`));
                              invalidate(org.api("/owners"), org.api("/properties"));
                              toast("Fin de qualité enregistrée ; la personne reste dans l’historique.");
                            } catch (e: any) {
                              toast(e.message, "erreur");
                            }
                          }}
                        >
                          Mettre fin
                        </Button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <Card
        title="Avances personnelles et remboursements"
        actions={
          canEdit && activeMembers.length ? (
            <Button small variant="secondaire" onClick={() => setContribution(true)}>
              Enregistrer une opération
            </Button>
          ) : null
        }
      >
        <Notice>{o.notice}</Notice>
        {o.balances.length ? (
          <Table caption="Soldes par personne">
            <thead>
              <tr>
                <th>Personne</th>
                <th className="right">Avances</th>
                <th className="right">Remboursements</th>
                <th className="right">Reste dû à la personne</th>
              </tr>
            </thead>
            <tbody>
              {o.balances.map((b) => (
                <tr key={b.member_id}>
                  <td>{b.member_name}</td>
                  <td className="right num">{money(b.advances_cents)}</td>
                  <td className="right num">{money(b.refunds_cents)}</td>
                  <td className="right num">{money(b.balance_cents)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
        {o.contributions.length === 0 ? (
          <p className="muted">Aucune opération enregistrée.</p>
        ) : (
          <Table caption="Opérations">
            <thead>
              <tr>
                <th>Date</th>
                <th>Personne</th>
                <th>Opération</th>
                <th className="right">Montant</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {o.contributions.map((c) => (
                <tr key={c.id}>
                  <td className="small">{date(c.occurred_on)}</td>
                  <td>{c.member_name}</td>
                  <td className="small">
                    {c.kind === "avance" ? "Avance" : "Remboursement"} — {c.justification}
                    {c.property_label ? <span className="pat-sub">{c.property_label}</span> : null}
                    {c.cancelled_at ? <span className="pat-sub">Annulée : {c.cancel_reason}</span> : null}
                  </td>
                  <td className="right num">{c.cancelled_at ? <s>{money(c.amount_cents)}</s> : money(c.amount_cents)}</td>
                  <td className="right">
                    {canEdit && !c.cancelled_at ? (
                      <Button small variant="discret" onClick={() => setCancelling(c.id)}>
                        Annuler
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <Card title="Pièces du propriétaire">
        {o.documents.length === 0 ? <p className="muted">Aucune pièce (statuts, Kbis, procès-verbaux, mandats).</p> : null}
        {o.documents.map((d) => (
          <FileLine key={d.id} name={d.title || d.filename} size={d.size_bytes} href={d.url} right={<span className="small muted">{date(d.created_at)}</span>} />
        ))}
        {canEdit ? (
          <div className="stack-sm">
            <Select
              label="Type de pièce"
              value={docKind}
              options={[
                { value: "statuts", label: "Statuts" },
                { value: "extrait_kbis", label: "Extrait Kbis" },
                { value: "proces_verbal", label: "Procès-verbal de décision" },
                { value: "mandat", label: "Mandat ou pouvoir" },
                { value: "justificatif_operation", label: "Justificatif d’avance ou de remboursement" },
                { value: "autre", label: "Autre pièce" },
              ]}
              onChange={(e) => setDocKind(e.target.value)}
            />
            <FileDrop
              busy={busy}
              onFiles={async (files) => {
                if (!files[0]) return;
                setBusy(true);
                try {
                  const form = new FormData();
                  form.append("kind", docKind);
                  form.append("file", files[0]);
                  await upload(org.api(`/owners/${o.id}/documents`), form);
                  invalidate(org.api(`/owners/${o.id}`));
                  toast("Pièce ajoutée.");
                } catch (e: any) {
                  toast(e.message, "erreur");
                } finally {
                  setBusy(false);
                }
              }}
            />
          </div>
        ) : null}
      </Card>
      <Modal open={editing} onClose={() => setEditing(false)} title="Modifier le propriétaire">
        {editing ? <OwnerForm owner={o} onDone={() => setEditing(false)} onCancel={() => setEditing(false)} /> : null}
      </Modal>
      <Modal open={!!member} onClose={() => setMember(null)} title={member === "nouveau" ? "Ajouter une personne" : "Modifier la personne"}>
        {member ? <MemberForm ownerId={o.id} member={member === "nouveau" ? undefined : member} documents={o.documents} onDone={() => setMember(null)} /> : null}
      </Modal>
      <Modal open={contribution} onClose={() => setContribution(false)} title="Enregistrer une avance ou un remboursement">
        {contribution ? <ContributionForm owner={o} onDone={() => setContribution(false)} /> : null}
      </Modal>
      <Modal open={!!cancelling} onClose={() => setCancelling(null)} title="Annuler une opération">
        {cancelling ? <CancelForm ownerId={o.id} contributionId={cancelling} onDone={() => setCancelling(null)} /> : null}
      </Modal>
    </div>
  );
}

function MemberForm({ ownerId, member, documents, onDone }: { ownerId: string; member?: Member; documents: OwnerDetail["documents"]; onDone: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [f, setF] = useState({
    full_name: member?.full_name ?? "",
    email: member?.email ?? "",
    phone: member?.phone ?? "",
    role: member?.role ?? "associe",
    share_pct: member?.share_milli !== null && member?.share_milli !== undefined ? String(member.share_milli / 1000).replace(".", ",") : "",
    parts_count: str(member?.parts_count),
    can_sign_leases: member?.can_sign_leases ?? false,
    can_collect_rents: member?.can_collect_rents ?? false,
    can_order_works: member?.can_order_works ?? false,
    receives_reports: member?.receives_reports ?? false,
    powers_note: member?.powers_note ?? "",
    mandate_from: member?.mandate_from ?? "",
    mandate_until: member?.mandate_until ?? "",
    document_id: "",
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const err = (k: string) => fieldError(error, k);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const share = numOrNull(f.share_pct);
    const body = {
      ...f,
      share_pct: share,
      parts_count: f.parts_count ? Number(f.parts_count) : null,
      mandate_from: f.mandate_from || null,
      mandate_until: f.mandate_until || null,
      document_id: f.document_id || null,
    };
    try {
      if (member) await put(org.api(`/owners/${ownerId}/members/${member.id}`), body);
      else await post(org.api(`/owners/${ownerId}/members`), body);
      invalidate(org.api("/owners"), org.api("/properties"));
      toast("Personne enregistrée.");
      onDone();
    } catch (e2) {
      setError(e2);
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="stack" onSubmit={submit} noValidate>
      <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
      <div className="form-row">
        <Input label="Nom et prénom" required value={f.full_name} error={err("full_name")} onChange={(e) => setF({ ...f, full_name: e.target.value })} />
        <Select label="Qualité" value={f.role} options={ROLES} error={err("role")} onChange={(e) => setF({ ...f, role: e.target.value })} />
      </div>
      <div className="form-row">
        <Input label="Adresse e-mail" type="email" value={f.email} error={err("email")} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Input label="Téléphone" type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
      </div>
      <div className="form-row">
        <Input label="Part détenue (%)" inputMode="decimal" value={f.share_pct} error={err("share_pct")} onChange={(e) => setF({ ...f, share_pct: e.target.value })} />
        <Input label="Nombre de parts sociales" inputMode="numeric" value={f.parts_count} error={err("parts_count")} onChange={(e) => setF({ ...f, parts_count: e.target.value })} />
      </div>
      <fieldset className="pat-fieldset">
        <legend>Pouvoirs déclarés</legend>
        <p className="small muted">Cochez seulement ce que les statuts, une décision ou un mandat prévoient.</p>
        <Checkbox label="Signer les baux" checked={f.can_sign_leases} onChange={(v) => setF({ ...f, can_sign_leases: v })} />
        <Checkbox label="Encaisser les loyers" checked={f.can_collect_rents} onChange={(v) => setF({ ...f, can_collect_rents: v })} />
        <Checkbox label="Commander des travaux" checked={f.can_order_works} onChange={(v) => setF({ ...f, can_order_works: v })} />
        <Checkbox label="Reçoit les rapports" checked={f.receives_reports} onChange={(v) => setF({ ...f, receives_reports: v })} />
        <Input label="Fondement des pouvoirs" placeholder="Ex. article 15 des statuts, procès-verbal du 12 mars 2025" value={f.powers_note} onChange={(e) => setF({ ...f, powers_note: e.target.value })} />
        <div className="form-row">
          <Input label="Mandat à partir du" type="date" value={f.mandate_from} error={err("mandate_from")} onChange={(e) => setF({ ...f, mandate_from: e.target.value })} />
          <Input label="Jusqu’au" type="date" value={f.mandate_until} error={err("mandate_until")} onChange={(e) => setF({ ...f, mandate_until: e.target.value })} />
        </div>
        {documents.length ? (
          <Select
            label="Pièce justificative"
            value={f.document_id}
            options={[{ value: "", label: "Aucune" }, ...documents.map((d) => ({ value: d.id, label: d.title || d.filename }))]}
            onChange={(e) => setF({ ...f, document_id: e.target.value })}
          />
        ) : null}
      </fieldset>
      <div className="form-actions">
        <Button variant="discret" onClick={onDone}>
          Annuler
        </Button>
        <Button type="submit" busy={busy}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

function ContributionForm({ owner, onDone }: { owner: OwnerDetail; onDone: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const members = owner.members.filter((m) => !m.ended_at);
  const [f, setF] = useState({ member_id: members[0]?.id ?? "", kind: "avance", amount_cents: null as number | null, occurred_on: today(), justification: "", property_id: "", document_id: "" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const err = (k: string) => fieldError(error, k);
  return (
    <form
      className="stack"
      noValidate
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await post(org.api(`/owners/${owner.id}/contributions`), { ...f, amount_cents: f.amount_cents ?? 0, property_id: f.property_id || null, document_id: f.document_id || null });
          invalidate(org.api(`/owners/${owner.id}`));
          toast("Opération enregistrée.");
          onDone();
        } catch (e2) {
          setError(e2);
        } finally {
          setBusy(false);
        }
      }}
    >
      <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
      <div className="form-row">
        <Select label="Personne" value={f.member_id} options={members.map((m) => ({ value: m.id, label: m.full_name }))} error={err("member_id")} onChange={(e) => setF({ ...f, member_id: e.target.value })} />
        <Select
          label="Opération"
          value={f.kind}
          options={[
            { value: "avance", label: "Avance faite par la personne" },
            { value: "remboursement", label: "Remboursement à la personne" },
          ]}
          onChange={(e) => setF({ ...f, kind: e.target.value })}
        />
      </div>
      <div className="form-row">
        <MoneyInput label="Montant" required cents={f.amount_cents} error={err("amount_cents")} onChange={(c) => setF({ ...f, amount_cents: c })} />
        <Input label="Date" type="date" required value={f.occurred_on} error={err("occurred_on")} onChange={(e) => setF({ ...f, occurred_on: e.target.value })} />
      </div>
      <Input label="Objet" required placeholder="Ex. taxe foncière 2026 payée personnellement" value={f.justification} error={err("justification")} onChange={(e) => setF({ ...f, justification: e.target.value })} />
      {owner.properties.length ? (
        <Select
          label="Bien concerné (facultatif)"
          value={f.property_id}
          options={[{ value: "", label: "Aucun en particulier" }, ...owner.properties.map((p) => ({ value: p.id, label: p.label }))]}
          error={err("property_id")}
          onChange={(e) => setF({ ...f, property_id: e.target.value })}
        />
      ) : null}
      {owner.documents.length ? (
        <Select
          label="Justificatif (facultatif)"
          value={f.document_id}
          options={[{ value: "", label: "Aucun" }, ...owner.documents.map((d) => ({ value: d.id, label: d.title || d.filename }))]}
          onChange={(e) => setF({ ...f, document_id: e.target.value })}
        />
      ) : null}
      <div className="form-actions">
        <Button variant="discret" onClick={onDone}>
          Annuler
        </Button>
        <Button type="submit" busy={busy}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

function CancelForm({ ownerId, contributionId, onDone }: { ownerId: string; contributionId: string; onDone: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  return (
    <div className="stack">
      <p>L’opération reste visible, barrée, avec le motif d’annulation. Elle n’entre plus dans les soldes.</p>
      <Input label="Motif" required value={reason} error={fieldError(error, "reason")} onChange={(e) => setReason(e.target.value)} />
      <ErrorBox error={error && !fieldError(error, "reason") ? error : null} />
      <div className="form-actions">
        <Button variant="discret" onClick={onDone}>
          Retour
        </Button>
        <Button
          onClick={async () => {
            setError(null);
            try {
              await post(org.api(`/owners/${ownerId}/contributions/${contributionId}/cancel`), { reason });
              invalidate(org.api(`/owners/${ownerId}`));
              toast("Opération annulée.");
              onDone();
            } catch (e) {
              setError(e);
            }
          }}
        >
          Annuler l’opération
        </Button>
      </div>
    </div>
  );
}
